<script setup lang="ts">
// Compact public language menu (guest headers/footers). Lists the ENABLED locales by native name;
// picking one goes through `switchLocale` (cookie + profile, and `/<locale>` URL on localized
// routes). Renders nothing when only one locale is enabled or on staff tools. Server output
// depends only on the URL + runtime config, so it is safe on SWR-cached pages.
import type { LocaleCode } from '~/constants/i18n';
import { getLocaleMeta } from '~/utils/i18n/locale';

const { tone = 'light', placement = 'bottom' } = defineProps<{
  /** `dark` for dark backgrounds (e.g. the landing footer). */
  tone?: 'light' | 'dark';
  /** Where the list opens relative to the trigger. */
  placement?: 'bottom' | 'top';
}>();

const { t } = useI18n();
const { enabledLocales, currentLocale, canSwitchLocale, switchLocale } = useLocaleSwitch();

const root = ref<HTMLElement | null>(null);
const trigger = ref<HTMLButtonElement | null>(null);
const isOpen = ref(false);
const listId = useId();

const options = computed(() =>
  enabledLocales.value.map((code) => {
    const meta = getLocaleMeta(code);
    return { code, name: meta.name, language: meta.language, isCurrent: code === currentLocale.value };
  }),
);

const triggerClass = computed(() =>
  tone === 'dark'
    ? 'border-white/[0.12] text-white/80 hover:border-white/30 hover:text-white'
    : 'border-black-10 text-black-100 hover:border-black-20 hover:bg-black-03',
);

const close = () => {
  isOpen.value = false;
};

// Disclosure pattern: Escape closes the list and hands focus back to the trigger.
const closeAndFocusTrigger = () => {
  if (!isOpen.value) return;
  close();
  trigger.value?.focus();
};

const select = async (code: LocaleCode) => {
  close();
  if (code === currentLocale.value) return;
  await switchLocale(code);
};

onClickOutside(root, close);
</script>

<template>
  <div
    v-if="canSwitchLocale"
    ref="root"
    class="relative"
    @keydown.esc="closeAndFocusTrigger"
  >
    <button
      ref="trigger"
      type="button"
      data-testid="locale-menu-trigger"
      class="inline-flex h-10 items-center gap-1.5 rounded-[10px] border px-2.5 text-[13px] font-semibold transition-colors"
      :class="triggerClass"
      :aria-label="t('common.preferences.language')"
      :aria-controls="listId"
      :aria-expanded="isOpen"
      @click="isOpen = !isOpen"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <circle
          cx="12"
          cy="12"
          r="9"
        />
        <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
      </svg>
      <span class="hidden tablet:inline">{{ currentLocale.toUpperCase() }}</span>
    </button>

    <ul
      v-if="isOpen"
      :id="listId"
      :aria-label="t('common.preferences.language')"
      class="absolute end-0 z-[110] min-w-[168px] overflow-hidden rounded-[12px] border border-black-10 bg-white py-1.5 shadow-[0_12px_32px_rgba(15,21,29,0.14)]"
      :class="placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'"
    >
      <li
        v-for="option in options"
        :key="option.code"
      >
        <button
          type="button"
          :aria-current="option.isCurrent ? 'true' : undefined"
          :lang="option.language"
          :data-testid="`locale-menu-option-${option.code}`"
          class="flex w-full items-center justify-between gap-3 px-4 py-2 text-start text-[14px] text-black-100 transition-colors hover:bg-black-03"
          :class="option.isCurrent ? 'font-semibold' : 'font-medium'"
          @click="select(option.code)"
        >
          <span>{{ option.name }}</span>
          <svg
            v-if="option.isCurrent"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.4"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-gold-primary-120"
            aria-hidden="true"
          >
            <path d="M5 12.5l4.5 4.5L19 7.5" />
          </svg>
        </button>
      </li>
    </ul>
  </div>
</template>
